import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import EditIcon from '@mui/icons-material/Edit';

import type { CatalogUploadValidation } from '@src/apis/reware/catalog/validateCatalogUpload';

import { SOURCE_UPLOAD_TYPE_LABELS } from './constants';
import type { UploadOptions } from './types';
import PreviewRecordsTable from './PreviewRecordsTable';

interface ValidationResultsProps {
  options: UploadOptions;
  validation: CatalogUploadValidation;
  onEditOptions: () => void;
  onCancel: () => void;
  onBeginUpload: () => void;
  isUploading: boolean;
  previewPageSize?: number;
  onPreviewNextPage?: () => void;
  onPreviewPreviousPage?: () => void;
  onPreviewPageSizeChange?: (pageSize: number) => void;
}

const ValidationResults: React.FC<ValidationResultsProps> = ({
  options,
  validation,
  onEditOptions,
  onCancel,
  onBeginUpload,
  isUploading,
  previewPageSize,
  onPreviewNextPage,
  onPreviewPreviousPage,
  onPreviewPageSizeChange,
}) => {
  const uploadTypeLabel = SOURCE_UPLOAD_TYPE_LABELS[options.uploadType] ?? options.uploadType;
  const processLabel = options.scheduleAt ? 'Scheduled' : 'Process immediately';
  const hasErrors = !validation.valid || validation.errors.length > 0;

  return (
    <Box>
      {/* Upload Options Summary */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Upload Options
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {'Name: '}
              {options.uploadName}
              {' \u00b7 Type: '}
              {uploadTypeLabel}
              {' Records \u00b7 '}
              {processLabel}
            </Typography>
          </Box>
          <IconButton onClick={onEditOptions} size="small">
            <EditIcon />
          </IconButton>
        </Box>
      </Paper>

      {/* CSV Validation Results */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          {hasErrors ?
            <ErrorOutlineIcon color="error" /> :
            <CheckCircleOutlineIcon color="success" />}
          <Typography variant="h6">CSV Validation Results</Typography>
        </Box>

        <Typography variant="body2" sx={{ mb: 2 }}>
          {hasErrors ? 'File has validation errors' : 'File analyzed successfully'}
        </Typography>

        {validation.errors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {validation.errors.map((err, i) => (
              <Typography key={i} variant="body2">{err}</Typography>
            ))}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
          <Chip label={`${validation.rowCount} rows`} size="small" variant="outlined" />
          <Chip label={`${validation.columnCount} columns`} size="small" variant="outlined" />
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Columns Detected:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 3 }}>
          {validation.columns.map(col => (
            <Chip key={col} label={col} size="small" variant="outlined" />
          ))}
        </Box>
      </Box>

      {/* Expected Outcome */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Expected Outcome
        </Typography>
        <Box sx={{ display: 'flex', gap: 4 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Variant SKUs to be Created
            </Typography>
            <Typography variant="h6" color="success.main">
              {validation.expectedCreates}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Variant SKUs to be Updated
            </Typography>
            <Typography variant="h6" color="info.main">
              {validation.expectedUpdates}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Preview Records Table */}
      {validation.previewRecords && validation.previewRecords.edges.length > 0 && (
        <PreviewRecordsTable
          previewRecords={validation.previewRecords}
          totalValidationErrors={validation.totalValidationErrors}
          pageSize={previewPageSize}
          onNextPage={onPreviewNextPage}
          onPreviousPage={onPreviewPreviousPage}
          onPageSizeChange={onPreviewPageSizeChange}
        />
      )}

      <Divider sx={{ mb: 2 }} />

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button onClick={onCancel} disabled={isUploading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onBeginUpload}
          disabled={isUploading || hasErrors}
        >
          {isUploading ? 'Uploading...' : 'Begin Upload'}
        </Button>
      </Box>
    </Box>
  );
};

export default ValidationResults;
